/** Panelden yazılan CSS'in tutunacağı sabit kimlikler.
 *
 * Panelden stil yazmanın önündeki asıl engel seçicidir: yüzeyler Tailwind
 * utility'leriyle çizilir, yani yöneticinin tarayıcıda gördüğü sınıf adları
 * (`flex`, `rounded-xl`) ne kararlıdır ne de anlamlıdır — bir sürüm sonra
 * değişirler ve kural sessizce çalışmaz olur. Bu dosya sitenin ana
 * bölgelerine kararlı birer kimlik verir ve **bir API'dir**: kurulumların
 * panelinde yazılmış CSS ona bağlıdır.
 *
 * **Bir adı yeniden adlandırmayın.** Değiştirmek, o kurulumların görünümünü
 * sessizce bozar — yöneticinin yazdığı kural hiçbir hata vermeden çalışmaz
 * olur. Yeni bir yüzey eklerken listeye **ekleme** yapılır.
 *
 * İki biçim vardır ve ayrım işe yarar:
 *
 * * **Tekil bölgeler `id` alır.** `id` seçicisinin özgüllüğü (0,1,0,0) bir
 *   utility sınıfını (0,0,1,0) ezer, yani yöneticinin kuralı `!important`
 *   istemeden tutar (bkz. CLAUDE.md → *Giriş kutusu ölçüleri Tailwind'den
 *   değil CSS'ten gelir*).
 * * **Tekrarlayan öğeler `data-bhs` alır.** Aynı yüzey sayfada onlarca kez
 *   çizilir; `id` tekil olmak zorundadır.
 */

/** Tekil kabuk bölgeleri. Altı şemanın kabuğu da bu kimlikleri kullanır. */
export const SHELL_IDS = {
  header: 'bhs-header',
  nav: 'bhs-nav',
  main: 'bhs-main',
  footer: 'bhs-footer',
  bottomBar: 'bhs-bottom-bar',
};

export const BHS_ATTRIBUTE = 'data-bhs';

/** `<body>` üzerindeki sayfa niteliği.
 *
 * Sayfaya özel kural yazmanın tek yolu budur (`body[data-bhs-page="slot"] …`)
 * ve rota başına bir sınıf üretmekten okunurdur.
 *
 * **Şemanın karşılığı burada değildir:** tasarım şeması `<html>` üzerinde
 * `data-site-theme` olarak zaten durur (`lib/theme.js`) ve o nitelik CSS'te
 * kullanılabilir. İkinci bir kaynak üretmek, biri unutulduğunda yöneticinin
 * kuralını sessizce çalışmaz yapardı.
 */
export const BODY_PAGE_ATTRIBUTE = 'data-bhs-page';

/** Tanınan `data-bhs` değerleri.
 *
 * Liste hem panelin seçici referansının hem kılavuzun kaynağıdır; buraya
 * yazılmayan bir ad yöneticiye hiçbir yerde görünmez.
 */
export const HOOKS = Object.freeze({
  GAME_CARD: 'game-card',
  GAME_GRID: 'game-grid',
  SLIDER: 'slider',
  CATEGORY_CARD: 'category-card',
  BET_SLIP: 'bet-slip',
  AUTH_MODAL: 'auth-modal',
  SECTION: 'section',
  CONTENT_PAGE: 'content-page',
  // Hesap bölümünün kökü (`/hesap/*`, AccountLayout). Alt sayfanın adı
  // `data-bhs-account-page`, içindeki alanlar `data-bhs-account` taşır.
  ACCOUNT: 'account',
  // Bonus kampanyasının kartı (Bonuslar sayfası, Betrun ana sayfa şeridi) ve
  // detay penceresi. İçerideki alanlar `data-bhs-bonus` taşır.
  BONUS_CARD: 'bonus-card',
  BONUS_MODAL: 'bonus-modal',
  // Oyunun kendi sayfası (`/oyun/:id`, bugün yalnızca Betrun). İçerideki
  // alanlar `data-bhs-game-page` taşır.
  GAME_PAGE: 'game-page',
  // Panelden yönetilen karşılama penceresi (components/WelcomePopup.jsx).
  // İçerideki alanlar sınıf adıyla seçilir: `.welcome-popup-title` …
  WELCOME_POPUP: 'welcome-popup',
  // Oyuna basınca açılan seçim penceresi (components/GameChoiceModal.jsx):
  // Giriş Yap / Kayıt Ol / Eğlenmek İçin Oyna, üyede Oyna / Demo. İçerideki
  // alanlar `data-bhs-game-choice` taşır.
  GAME_CHOICE_MODAL: 'game-choice-modal',
});

const KNOWN_HOOKS = new Set(Object.values(HOOKS));

/** Vitrin bölümünün adı: `[data-bhs-section="popular"]`.
 *
 * Bölümler `data-bhs="section"` ortak niteliğini **ve** kendi adını taşır:
 * ortak nitelik "bütün şeritlerin başlığı" gibi toplu kuralı, ad ise tek bir
 * şeridi seçer. Ad şeridin **başlığından değil işlevinden** türer — Paris'te
 * "Trend", Zümrüt'te "En Popüler", Vega'da "Popüler Oyunlar" yazan şeritlerin
 * üçü de `popular`'dır; başlık panelden ya da şemadan değişse de kural tutar.
 *
 * Kimlik kuralı burada da geçerlidir: ad bir API'dir, yeniden adlandırılmaz.
 */
export const SECTION_ATTRIBUTE = 'data-bhs-section';

export const SECTIONS = Object.freeze({
  PICKS: 'picks',
  POPULAR: 'popular',
  SLOTS: 'slots',
  LIVE_CASINO: 'live-casino',
  NEW: 'new',
  FAST: 'fast',
  BONUSES: 'bonuses',
  MATCHES: 'matches',
  PROVIDERS: 'providers',
  WINS: 'wins',
  LIVE_BETS: 'live-bets',
  FAQ: 'faq',
  FEATURED: 'featured',
  CATEGORIES: 'categories',
  SHORTCUTS: 'shortcuts',
  SPONSORS: 'sponsors',
  // Ana sayfanın en üstündeki banner alanı (slider ya da Herkül'ün kartları).
  // Alan adı `slider`dan ayrıdır: `[data-bhs="slider"]` banner'ın kendisini,
  // `[data-bhs-section="hero"]` onu taşıyan bölümü seçer.
  HERO: 'hero',
});

const KNOWN_SECTIONS = new Set(Object.values(SECTIONS));

/** İçerik sayfasının adresi: `[data-bhs="content-page"][data-bhs-slug="…"]`.
 *
 * Footer bağlantılarının açtığı sayfalar (`/sayfa/<adres>`) tek bileşendir;
 * `body[data-bhs-page="page"]` hepsini birden seçer, bu nitelik ise tek
 * birini. Değer panelde verilen sayfa adresidir — adres değişirse kural da
 * değişmelidir, çünkü bağlantının kendisi de değişmiştir.
 */
export const CONTENT_PAGE_SLUG_ATTRIBUTE = 'data-bhs-slug';

/** Hesap bölümünün alanları: `[data-bhs-account="wager"]`.
 *
 * Hesap sayfaları (Profilim, para yatır/çek, geçmişler …) utility sınıflarıyla
 * ve satır içi stille çizilir; panelden yazılan CSS'in tutunacağı tek kararlı
 * yer bu niteliktir. Üç katmanlıdır ve birlikte okunur:
 *
 * * `[data-bhs="account"]` — bölümün kökü, her hesap sayfasında aynıdır;
 * * `[data-bhs-account-page="profile"]` — aynı kökte, açık olan alt sayfa
 *   (`ACCOUNT_PAGES`; adres Türkçedir, ad değildir — `pageKey.accountPageKey`);
 * * `[data-bhs-account="…"]` — sayfanın içindeki alan (`ACCOUNT_PARTS`).
 *
 * Alan adları sayfalar arasında **ortaktır** ve işlevi söyler: `panel` her
 * sayfanın ana kutusu, `page-header` başlığı, `row` bir listenin satırı,
 * `progress` bir ilerleme çubuğu. Sayfaya özel bir kural alt sayfa adıyla
 * daraltılır (`[data-bhs-account-page="deposit"] [data-bhs-account="panel"]`).
 * Form alanları ayrıca `data-bhs-field="email"` taşır.
 *
 * Kimlik kuralı burada da geçerlidir: ad bir API'dir, yeniden adlandırılmaz.
 */
export const ACCOUNT_ATTRIBUTE = 'data-bhs-account';
export const ACCOUNT_PAGE_ATTRIBUTE = 'data-bhs-account-page';
export const FIELD_ATTRIBUTE = 'data-bhs-field';

export const ACCOUNT_PAGES = Object.freeze({
  PROFILE: 'profile',
  SECURITY: 'security',
  KYC: 'kyc',
  DEPOSIT: 'deposit',
  DEPOSIT_PAYMENT: 'deposit-payment',
  WITHDRAW: 'withdraw',
  WITHDRAWAL_ACCOUNT: 'withdrawal-account',
  MESSAGES: 'messages',
  ACTIVITY: 'activity',
  GAME_HISTORY: 'game-history',
  COUPONS: 'coupons',
  LOSS_BONUS: 'loss-bonus',
  AFFILIATE: 'affiliate',
  PROMO_CODE: 'promo-code',
  MY_BONUSES: 'my-bonuses',
});

export const ACCOUNT_PARTS = Object.freeze([
  // Kabuk (AccountLayout): sol sütun, üye kartı, menü ve içerik alanı.
  'sidebar', 'user-card', 'avatar', 'username', 'balance', 'bonus-balance',
  'menu', 'menu-item', 'logout', 'content', 'money-switch', 'money-tab',
  // Her sayfada ortak alanlar.
  'panel', 'page-header', 'header', 'title', 'subtitle', 'back', 'card',
  'tabs', 'tab', 'filters', 'form', 'field', 'submit', 'actions', 'list',
  'row', 'table', 'empty', 'loading', 'pagination', 'summary', 'stat',
  'badge', 'notice', 'terms', 'progress', 'progress-bar', 'wager', 'modal',
  // Sayfaya özel alanlar.
  'security-callout', 'two-factor', 'self-exclusion', 'code', 'rewards', 'bonus-claims',
  'freespin-rights', 'freebet-rights', 'methods', 'method', 'amount',
  'quick-amounts', 'payment-details', 'detail', 'copy', 'payment-link',
  'instructions', 'legs', 'leg', 'cashout', 'message', 'tiers', 'tier',
  'referral', 'chart', 'chart-bar', 'withdraw',
  // Güvenlik sayfası: istenen belgeler ve sorumlu oyun limitleri.
  'documents', 'document', 'upload', 'limits', 'limit',
]);

const KNOWN_ACCOUNT_PARTS = new Set(ACCOUNT_PARTS);

/** Bonus kartının ve detay penceresinin alanları: `[data-bhs-bonus="headline"]`.
 *
 * Kart ile pencere **aynı alan adlarını** kullanır (görselin üstündeki büyük
 * ödül başlığı ikisinde de `headline`, açıklama ikisinde de `description`);
 * yalnızca birine kural yazmak için kök daraltılır:
 * `[data-bhs="bonus-modal"] [data-bhs-bonus="headline"]`. Tek bir kampanyayı
 * seçmek için kart ve pencere ayrıca `data-bhs-bonus-id` taşır — değer
 * kampanyanın kimliğidir, panelde kampanya silinip yeniden açılırsa değişir.
 *
 * Kimlik kuralı burada da geçerlidir: ad bir API'dir, yeniden adlandırılmaz.
 */
export const BONUS_ATTRIBUTE = 'data-bhs-bonus';
export const BONUS_ID_ATTRIBUTE = 'data-bhs-bonus-id';

export const BONUS_PARTS = Object.freeze([
  'overlay', 'media', 'image', 'badge', 'headline', 'body', 'title',
  'description', 'highlight', 'daily-limit', 'stats', 'stat', 'actions',
  'claim', 'detail', 'close', 'terms', 'tiers',
]);

const KNOWN_BONUS_PARTS = new Set(BONUS_PARTS);

/** Bonus alanına yayılan nitelik: `<div {...bonusHook('headline')}>`. */
export const bonusHook = (part) => {
  const value = String(part || '');
  if (!KNOWN_BONUS_PARTS.has(value) && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`Tanınmayan bonus alanı: ${value} (bkz. lib/domHooks.js)`);
  }
  return { [BONUS_ATTRIBUTE]: value };
};

/** Tek bir kampanyanın kimliği: `<div {...bonusIdHook(bonus.id)}>`. */
export const bonusIdHook = (id) => ({ [BONUS_ID_ATTRIBUTE]: String(id || '') });

/** Oyun sayfasının alanları: `[data-bhs-game-page="sidebar"]`.
 *
 * Sayfa kökü `data-bhs="game-page"` taşır; oyun penceresi (oynatıcı), altındaki
 * araç şeridi, sağdaki öneri sütunu, oyun bilgisi kartı (kapak görseli
 * `cover`) ve altındaki "Son Kazançlar" tablosu (`wins`, satırı `win-row`) bu
 * nitelikle adlandırılır. Sayfanın kendisi `<body data-bhs-page="game">`dir.
 */
export const GAME_PAGE_ATTRIBUTE = 'data-bhs-game-page';

export const GAME_PAGE_PARTS = Object.freeze([
  'main', 'player', 'stage', 'toolbar', 'mode-switch', 'info', 'sidebar',
  'sidebar-title', 'recommendations', 'recommendation', 'login', 'empty',
  'volatility', 'cover', 'wins', 'wins-badge', 'wins-empty', 'win-row',
]);

const KNOWN_GAME_PAGE_PARTS = new Set(GAME_PAGE_PARTS);

/** Oyun sayfası alanına yayılan nitelik: `<aside {...gamePageHook('sidebar')}>`. */
export const gamePageHook = (part) => {
  const value = String(part || '');
  if (!KNOWN_GAME_PAGE_PARTS.has(value) && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`Tanınmayan oyun sayfası alanı: ${value} (bkz. lib/domHooks.js)`);
  }
  return { [GAME_PAGE_ATTRIBUTE]: value };
};

/** Oyun seçim penceresinin alanları: `[data-bhs-game-choice="login"]`.
 *
 * Pencere kökü `data-bhs="game-choice-modal"` taşır ve oturum durumunu ayrıca
 * söyler: `data-bhs-game-choice-state="guest"` (Giriş Yap / Kayıt Ol) ya da
 * `"member"` (Oyna / Demo). İki görünüm aynı kökü paylaştığı için yalnızca
 * birine kural yazmanın yolu budur. Pencerenin arkasındaki katman kökün
 * dışındadır: `[data-bhs-game-choice="overlay"]` (tıklanınca kapanan tam
 * ekran kap) ve içindeki `backdrop` (karartma).
 */
export const GAME_CHOICE_ATTRIBUTE = 'data-bhs-game-choice';
export const GAME_CHOICE_STATE_ATTRIBUTE = 'data-bhs-game-choice-state';

export const GAME_CHOICE_PARTS = Object.freeze([
  'overlay', 'backdrop', 'close', 'media', 'image', 'shade', 'heading',
  'title', 'provider', 'body', 'message', 'balance', 'balance-row',
  'bonus-row', 'play', 'login', 'register', 'divider', 'demo', 'demo-note',
  'demo-unavailable', 'deposit-note',
]);

const KNOWN_GAME_CHOICE_PARTS = new Set(GAME_CHOICE_PARTS);

/** Seçim penceresi alanına yayılan nitelik: `<button {...gameChoiceHook('login')}>`. */
export const gameChoiceHook = (part) => {
  const value = String(part || '');
  if (!KNOWN_GAME_CHOICE_PARTS.has(value) && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`Tanınmayan oyun seçim alanı: ${value} (bkz. lib/domHooks.js)`);
  }
  return { [GAME_CHOICE_ATTRIBUTE]: value };
};

/** Hesap alanına yayılan nitelik: `<div {...accountHook('wager')}>`.
 *
 * İkinci değer verilirse form alanının adı da yazılır
 * (`accountHook('field', 'email')` → `data-bhs-field="email"`).
 */
export const accountHook = (part, field) => {
  const value = String(part || '');
  if (!KNOWN_ACCOUNT_PARTS.has(value) && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`Tanınmayan hesap alanı: ${value} (bkz. lib/domHooks.js)`);
  }
  const attrs = { [ACCOUNT_ATTRIBUTE]: value };
  if (field) attrs[FIELD_ATTRIBUTE] = String(field);
  return attrs;
};

/** JSX'e yayılan nitelik: `<div {...hook(HOOKS.GAME_CARD)}>`.
 *
 * Bilinmeyen ad geliştirmede uyarı basar: sessizce yazılan bir yazım hatası,
 * panelde listelenen seçiciyle eşleşmeyen bir nitelik bırakır ve yöneticinin
 * kuralı hiçbir şeye tutunmaz.
 */
export const hook = (name) => {
  const value = String(name || '');
  if (!KNOWN_HOOKS.has(value) && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`Tanınmayan DOM kimliği: ${value} (bkz. lib/domHooks.js)`);
  }
  return { [BHS_ATTRIBUTE]: value };
};

/** Vitrin bölümüne yayılan iki nitelik: `<section {...sectionHook(SECTIONS.FAQ)}>`.
 *
 * Ad verilmezse boş nesne döner: şerit bileşenleri (`MeritSection` …) ad
 * almadan da kullanılır ve niteliksiz çizilmeleri doğrudur.
 */
export const sectionHook = (name) => {
  const value = String(name || '');
  if (!value) return {};
  if (!KNOWN_SECTIONS.has(value) && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`Tanınmayan bölüm kimliği: ${value} (bkz. lib/domHooks.js)`);
  }
  return { [BHS_ATTRIBUTE]: HOOKS.SECTION, [SECTION_ATTRIBUTE]: value };
};

/** Sayfa adını `<body>` üzerine yazar; boş ad niteliği kaldırır.
 *
 * Yazma mantığı bileşenden ayrıdır çünkü `PageMarker` `useLocation`'a bağlıdır
 * ve `react-router-dom` bu kurulumun jest resolver'ıyla çözülmez; ölçülmek
 * istenen şey ise niteliğin yazılıp **bırakılmadığıdır**.
 */
export const applyPageKey = (pageKey) => {
  const body = typeof document === 'undefined' ? null : document.body;
  if (!body) return;
  const value = String(pageKey || '');
  if (value) body.setAttribute(BODY_PAGE_ATTRIBUTE, value);
  else body.removeAttribute(BODY_PAGE_ATTRIBUTE);
};
